import React, { Component } from 'react';
import { Flex, Box } from 'grid-styled';
import styled from '@emotion/styled';
import { createRefetchContainer, graphql, RelayRefetchProp } from 'react-relay';
import AutoComplete from 'react-autocomplete';
import _get from 'lodash/get';
import memoize from 'memoize-one';
import Search from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { styles } from '@onehope/design-system';

/* Material UI */
import GiftIcon from 'material-ui/svg-icons/action/card-giftcard';
import WineIcon from 'material-ui/svg-icons/maps/local-bar';
import EventIcon from 'material-ui/svg-icons/action/event';
import PersonIcon from 'material-ui/svg-icons/social/person';
import CoffeeIcon from 'material-ui/svg-icons/maps/local-cafe';
import KitchenIcon from 'material-ui/svg-icons/maps/restaurant';
import CheckMarkIcon from 'material-ui/svg-icons/navigation/check';
/* Mutations */
import ShopWithCeoAddMutation from '../../../mutationsModern/User/ShopWithCeoAddMutation';
/* Helpers */
import { urlDefaultProfile } from '../../../../server/utils/urlConstants';
import { DropDownAutoComplete_user } from './__generated__/DropDownAutoComplete_user.graphql';
/* Styles */
import './DropDownAutoComplete.scss';
import { getImageUrl } from '../../../utils/helpers';

const {
  cssConstants: { font, neutral2, inputFocusColor, errorColor, primaryColor },
} = styles;

const autoCompleteStyles = {
  wrapperStyle: {
    width: '100%',
    position: 'relative',
    height: 'auto',
  },
};

const autoCompleteStylesMobile = {
  wrapperStyle: {
    position: 'relative',
    height: 'auto',
    width: '100%',
  },
};

const menuStyle = {
  menuStyle: {
    borderRadius: '0',
    boxShadow: '0 10px 20px 0 rgba(0,0,0,0.25)',
    background: '#fafafa',
    padding: '0',
    zIndex: 2000,
    overflowY: 'auto',
    maxHeight: '200px',
    width: '65%',
  },
};

const SearchInput = styled(TextField)`
  & .MuiInputBase-input {
    font-family: ${font};
    height: 43.22px;
    padding: 0.15rem 16px;
    font-weight: normal;
    line-height: 1.57;
    font-size: 14px;
    border-radius: 100px;
    background-color: ${neutral2};
  }
  & .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${primaryColor} !important;
    border-radius: 100px;
  }

  & .MuiInputBase-input:focus {
    background-color: ${inputFocusColor};
  }
  & .MuiInput-underline:after {
    border-bottom-color: ${primaryColor};
  }

  & .MuiFormHelperText-root {
    font-family: ${font};
    color: ${errorColor};
    font-size: 11px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.64;
    letter-spacing: normal;
    margin-top: 8px;
  }
  & .MuiInputAdornment-root {
    position: absolute;
    right: 16px;
  }
  & .MuiOutlinedInput-adornedEnd {
    padding-right: 0;
  }
`;

interface DropDownAutoCompleteProps {
  containerWidth: number;
  toggleSearch?: () => void;
  relay: RelayRefetchProp;
  customCeoId?: string | null;
  handleExitEvent?: (callback?: () => void) => void;
  handleRouteChange: (path: string | null) => void;
  user: DropDownAutoComplete_user;
  placeHolderText: string;
  searchEvents: boolean;
  searchProducts: boolean;
  showAllItemsOnFocus?: boolean;
  searchCEOS: boolean;
  updateCeo?: (input: string) => void;
  handleCartHotSwap: (input: () => void) => void;
  handleAuthentication: () => void;
  deactivateDropDown: () => void;
  includeShoppingWithCeoFragment: boolean;
  onSelectedEvent: (input: string | null, input2: string | null) => void;
}

interface DropDownAutoCompleteState {
  isMenuOpen: boolean;
  inputValue: string;
  selectedCeoId: string;
  isLoadingData: boolean;
  searchActivated: boolean;
  placeHolderText: string;
}

interface AutoCompleteDataNode {
  readonly id: string;
  readonly isCEO: boolean | null;
  readonly ceoId: string | null;
  readonly isEvent: boolean | null;
  readonly isProduct: boolean | null;
  readonly routePath: string | null;
  readonly primaryLabel: string | null;
  readonly secondaryLabel: string | null;
  readonly productCategory: string | null;
}
class DropDownAutoComplete extends Component<
  DropDownAutoCompleteProps,
  DropDownAutoCompleteState
> {
  state = {
    isMenuOpen: false,
    inputValue: '',
    selectedCeoId: '',
    isLoadingData: false,
    searchActivated: false,
    placeHolderText: this.props.placeHolderText,
  };

  getItems = memoize(
    (
      DropDownAutoCompleteData: { edges: { node: AutoCompleteDataNode }[] },
      publicId: string,
    ) => {
      return DropDownAutoCompleteData && DropDownAutoCompleteData.edges
        ? DropDownAutoCompleteData.edges.filter(edge => {
            const ceoId = _get(edge, 'node.ceoId');
            if (ceoId && ceoId === publicId) {
              return false;
            }
            return true;
          })
        : [];
    },
  );

  handleLoadingData = (isLoadingData: boolean) => {
    this.setState({ isLoadingData });
  };

  handleFocus = () => {
    const { inputValue } = this.state;
    if (inputValue === '') {
      const e = {
        target: {
          value: '',
        },
      };
      this.handleInputChange(e);
    }
  };

  handleSearchComplete = () => {
    const {
      placeHolderText,
      user: { DropDownAutoCompleteData, publicId },
    } = this.props;
    const { searchActivated } = this.state;
    this.handleLoadingData(false);
    const items = this.getItems(DropDownAutoCompleteData, publicId);
    const placeHolder =
      searchActivated && items.length === 0
        ? 'No results found.'
        : placeHolderText;
    this.setState({ placeHolderText: placeHolder });
  };

  handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    const { showAllItemsOnFocus } = this.props;
    if (!inputValue && !showAllItemsOnFocus) {
      this.setState({ inputValue });
      return this.closeMenu();
    }
    const { isLoadingData, isMenuOpen } = this.state;
    if (!isMenuOpen) {
      this.openMenu();
    }
    const {
      relay,
      searchEvents,
      searchProducts,
      searchCEOS,
      customCeoId,
      user,
    } = this.props;
    this.setState({ inputValue });
    if (!isLoadingData || inputValue === '') {
      this.handleLoadingData(true);
      const refetchVariables = {
        accountId: user.userId,
        ceoId: customCeoId,
        includeShoppingWithCeo: false,
        inputValue,
        searchEvents,
        searchProducts,
        searchCEOS,
      };

      relay.refetch(
        refetchVariables,
        undefined,
        () => this.handleSearchComplete(),
        {
          force: true,
        },
      );
    }
  };

  getProductIcon = (category: string) => {
    switch (category) {
      case 'wine':
        return <WineIcon color="black" />;
      case 'gift':
        return <GiftIcon color="black" />;
      case 'coffee':
        return <CoffeeIcon color="black" />;
      case 'kitchen':
        return <KitchenIcon color="black" />;
      default:
        return <WineIcon color="black" />;
    }
  };

  closeMenu = () => {
    this.setState({ isMenuOpen: false });
  };

  openMenu = () => {
    this.setState({ isMenuOpen: true });
  };

  clearInput = () => {
    this.setState({ inputValue: '' });
  };

  handleSelect = (val: string, item: { node: AutoCompleteDataNode }) => {
    const {
      handleRouteChange,
      user,
      customCeoId,
      onSelectedEvent,
    } = this.props;
    if (!user) return;
    const {
      routePath,
      isProduct,
      isEvent,
      isCEO,
      ceoId,
      primaryLabel,
    } = item.node;
    if ((!isCEO && !customCeoId && isProduct) || (!customCeoId && isEvent)) {
      this.closeMenu();
      this.clearInput();
      return handleRouteChange(routePath);
    }
    if (isCEO) {
      this.shopWithCeo(ceoId);
      this.clearInput();
      this.closeMenu();
      return;
    }
    if (customCeoId) {
      onSelectedEvent(routePath, primaryLabel);
      this.clearInput();
      this.closeMenu();
    }
  };

  exitEvent = () => {
    const { handleExitEvent } = this.props;
    if (handleExitEvent) handleExitEvent();
  };

  onRegistrationComplete = (ceoId: string) => {
    const { handleCartHotSwap, deactivateDropDown, updateCeo } = this.props;
    handleCartHotSwap(deactivateDropDown);
    if (updateCeo) updateCeo(ceoId);
    this.clearInput();
    this.exitEvent();
  };

  onRegistrationError = (errors: Object) => {
    console.log('error in register mutation', errors);
  };

  shopWithCeo = (ceoId: string | null) => {
    if (!ceoId) return;
    const {
      relay: { environment },
    } = this.props;
    const onCompleted = () => this.onRegistrationComplete(ceoId);
    const onError = this.onRegistrationError;
    return ShopWithCeoAddMutation.commit({
      ceoId,
      environment,
      onCompleted,
      onError,
    });
  };

  handleClick = () => {
    this.setState({ searchActivated: true });
    return this.props.showAllItemsOnFocus ? this.handleFocus() : null;
  };

  getProfileImage = (image: string) => {
    return image
      ? getImageUrl(image, 'thumb')
      : `${urlDefaultProfile}default_profile.svg`;
  };

  getPrimaryLabel = (label: string) => {
    const { containerWidth } = this.props;
    if (containerWidth < 600 && label.length > 24) {
      return `${label.slice(0, 23)}...`;
    }
    if (label.length > 45) {
      return `${label.slice(0, 44)}...`;
    }
    return label;
  };

  render() {
    const {
      containerWidth,
      user,
      user: { DropDownAutoCompleteData, publicId },
    } = this.props;
    const {
      inputValue,
      selectedCeoId,
      isMenuOpen,
      placeHolderText,
    } = this.state;
    if (!user) return null;
    const { shoppingWithCeoUser } = user;
    const autoCompleteStyle =
      containerWidth < 600 ? autoCompleteStylesMobile : autoCompleteStyles;
    const items = this.getItems(DropDownAutoCompleteData, publicId);
    return (
      <Box w={[1]} className="DropDownContainer">
        <AutoComplete
          items={items}
          {...menuStyle}
          {...autoCompleteStyle}
          open={isMenuOpen}
          value={inputValue}
          onSelect={this.handleSelect}
          onChange={this.handleInputChange}
          getItemValue={({ node: item }: any) => item.primaryLabel}
          renderInput={(props: any) => {
            return (
              <div onClick={this.handleClick}>
                <SearchInput
                  {...props}
                  fullWidth
                  variant="outlined"
                  placeholder={placeHolderText}
                  id="autocomplete"
                  name="autocomplete"
                  value={inputValue}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            );
          }}
          renderItem={({ node: item }: any, isHighlighted: boolean) => (
            <Flex
              key={item.id}
              align="center"
              justify="space-between"
              className="DropDownMenuItem"
              style={{
                background:
                  isHighlighted || item.id === selectedCeoId
                    ? 'white'
                    : '#fafafa',
              }}
            >
              <Box>
                <Flex align="center">
                  <Box className="DropDownIcon">
                    {item.isProduct &&
                      this.getProductIcon(item.productCategory)}
                    {item.isEvent && <EventIcon color="black" />}
                    {item.isCEO && (
                      <div>
                        {item.image ? (
                          <Flex align="center">
                            <Box className="DropDownMenuImageContainer">
                              <div className="media-photo profile-pic-background">
                                <img
                                  src={this.getProfileImage(item.image)}
                                  className="DropDownMenuProfileImage"
                                />
                              </div>
                              <div className="media-photo media-round">
                                <img
                                  src={this.getProfileImage(item.image)}
                                  className="DropDownMenuProfileImage"
                                />
                              </div>
                            </Box>
                          </Flex>
                        ) : (
                          <PersonIcon color="black" />
                        )}
                      </div>
                    )}
                  </Box>
                  <Box>
                    <Flex
                      column
                      justify="center"
                      className="DropDownLabelsContainer"
                    >
                      <Box className="DropDownPrimaryLabel">
                        {this.getPrimaryLabel(item.primaryLabel)}
                      </Box>
                      <Box className="DropDownSecondaryLabel">
                        {item.secondaryLabel}
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
              {item.isCEO && (
                <Box style={{ paddingRight: 22 }}>
                  {shoppingWithCeoUser &&
                    item.ceoId === shoppingWithCeoUser.publicId && (
                      <CheckMarkIcon color="black" />
                    )}
                </Box>
              )}
            </Flex>
          )}
        />
      </Box>
    );
  }
}

export default createRefetchContainer(
  DropDownAutoComplete,
  {
    user: graphql`
      fragment DropDownAutoComplete_user on User
        @argumentDefinitions(
          ceoId: { type: "String", defaultValue: "none" }
          inputValue: { type: "String", defaultValue: null }
          searchCEOS: { type: "Boolean", defaultValue: false }
          searchEvents: { type: "Boolean", defaultValue: false }
          searchProducts: { type: "Boolean", defaultValue: false }
        ) {
        ...AuthDialog_user
        id
        userId
        publicId
        permissions {
          id
          oneCommerce {
            id
            ceo {
              access
            }
          }
        }
        shoppingWithCeoUser {
          publicId
        }
        DropDownAutoCompleteData: autoCompleteData(
          first: 25
          ceoId: $ceoId
          inputValue: $inputValue
          searchCEOS: $searchCEOS
          searchEvents: $searchEvents
          searchProducts: $searchProducts
        ) @connection(key: "DropDownAutoComplete_DropDownAutoCompleteData") {
          edges {
            cursor
            node {
              id
              isCEO
              image
              ceoId
              isEvent
              isProduct
              routePath
              primaryLabel
              secondaryLabel
              productCategory
            }
          }
        }
      }
    `,
  },
  graphql`
    query DropDownAutoCompleteRefetchQuery(
      $accountId: String
      $ceoId: String
      $inputValue: String
      $searchCEOS: Boolean
      $searchEvents: Boolean
      $searchProducts: Boolean
    ) {
      viewer {
        id
        id
        userInfo(accountId: $accountId) {
          id
          ...DropDownAutoComplete_user
            @arguments(
              ceoId: $ceoId
              inputValue: $inputValue
              searchCEOS: $searchCEOS
              searchEvents: $searchEvents
              searchProducts: $searchProducts
            )
        }
      }
    }
  `,
);
